import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { StringEquipmentState } from 'app/modules/cdft/state/string-equipment.state';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { CDFTState } from 'app/modules/cdft/state/cdft.state';
import { CDFTService } from 'app/modules/cdft/services/cdft.service';
import { first, isEmpty, last } from 'lodash';
import { WellStringState } from 'app/modules/cdft/state/well-string.state';
import { WellString } from 'app/modules/cdft/model';
import module from 'app/modules/cdft/cdft.module';
import { WellStringStatusState } from '../../state/well-string-status.state';

@Component({
    selector: 'sssv-barrier',
    templateUrl: './sssv-barrier.component.html',
    standalone: true,
    styleUrls: ['./sssv-barrier.component.scss'],
    imports: module,
})
export class SssvBarrierComponent {
    private subscriptions: Subscription = new Subscription();
    @Input() isViewOnly: boolean;
    formGroup: FormGroup;

    isSSSV = true;
    wellString: WellString;
    wrsvTypes: any;
    wrsvType: string
    typeOfDownholeBarrier: string

    leakRateTable = {
        time: '30 mins',
        water: {
            leak_rate_412: 170,
            leak_rate_312: 290,
            leak_rate_278: 440,
            leak_rate_238: 660,
        },
        gas: {
            leak_rate_412: '',
            leak_rate_312: '',
            leak_rate_278: '',
            leak_rate_238: '',
        },
    };

    constructor(
        private formBuilder: FormBuilder,
        public state: CDFTState,
        private stringEquipmentState: StringEquipmentState,
        public service: CDFTService,
        public wellStringState: WellStringState,
        private wellStringStatusState: WellStringStatusState,
    ) { }

    get cdftTimeTubingPressure() {
        return this.formGroup.get('cdftTimeTubingPressure') as FormArray;
    }

    ngOnInit() {
        this.wrsvTypes = this.service.getSSSVOptionType();
        this.formGroup = this.onFormGroup();
        this.state.setFormGroup('cdftSssvDbt', this.formGroup);

        const stringEquipment$ = this.stringEquipmentState.subscribe(
            async (stringEquipment: StringEquipmentState) => {
                this.isSSSV = isEmpty(stringEquipment.wrsv);

                const excludedKeys = ['cyclingTest', 'clPressure', 'hydReturn', 'slamTest', 'blowingGas'];
                const filteredValues = Object.fromEntries(
                    Object.entries(stringEquipment.toSSV()).filter(([key]) => !excludedKeys.includes(key))
                );

                this.formGroup.patchValue(filteredValues);

                // to recheck the sssv and wrsv 
                this.wrsvType = this.formGroup.get('wrsvType').value
                const ctrl = this.formGroup.get('typeOfDownholeBarrier');
                const type = this.wrsvType?.toLowerCase();


                // TODO: keep this code if client request change again
                // ctrl?.setValue(
                //     type === 'plug' ? 'downhole plug' :
                //         type === 'storm choke' ? 'storm choke' :
                //             type ? 'wr-scssv' : 'tr-scssv'
                // );

                ctrl?.setValue(
                    type == null || type === 'tr-scssv' || type === 'trcssv' || type === 'trscssv' ? 'trscssv' : 'retrieable valve/plug'
                );

                this.typeOfDownholeBarrier = this.formGroup.get('typeOfDownholeBarrier').value

                if (!this.isViewOnly && this.typeOfDownholeBarrier != 'trscssv') {
                    this.onTypeDownholeBarrierTested()
                }
            }
        );

        const wellString$ = this.wellStringState
            .subscribe(
                (wellString: WellString) => {
                    this.wellString = wellString;
                    this.onLeakRate();
                }
            );

        this.onPopulate();

        this.subscriptions.add(stringEquipment$);
        this.subscriptions.add(wellString$);
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    onSSSVResult() {
        if (isEmpty(this.wellString)) return;

        let timePressureDiff = -1;
        try {
            timePressureDiff = this.formGroup
                .get('valveLeakRate')
                .getRawValue();
        } catch (e) {
            return;
        }

        let threshold = Number.POSITIVE_INFINITY;
        switch (this.wellString.tubingSize) {
            case 2 + 3 / 8:
                threshold = 660;
                break;
            case 2 + 7 / 8:
                threshold = 440;
                break;
            case 3 + 1 / 2:
                threshold = 290;
                break;
            case 4 + 1 / 2:
                threshold = 170;
                break;
        }

        let sssvResult: string;
        let isDownholePlug = this.formGroup.get('wrsvType').value == 'Plug';

        // type for downhole plug 
        if (this.typeOfDownholeBarrier != 'trscssv' && isDownholePlug) {
            this.service.wellProdCategory$
                .subscribe(wellProd => {
                    const wellType = wellProd?.wellStringType?.toLowerCase();
                    const isOilOrWater = ['oil producer', 'water injector'].includes(wellType || '');
                    const isGas = ['gas producer', 'gas injector'].includes(wellType || '');

                    if (timePressureDiff == null || timePressureDiff < 0) sssvResult = 'UTT';
                    else if (
                        (isOilOrWater && timePressureDiff > 30) ||
                        (isGas && timePressureDiff > 0)
                    ) {
                        sssvResult = 'FAILED';
                    } else {
                        sssvResult = 'GOOD';
                    }

                    this.formGroup.patchValue({ sssvResult }, { emitEvent: false });
                    this.state.recalculate();
                });
        } else {
            if (timePressureDiff == null || timePressureDiff < 0) sssvResult = 'UTT'
            else if (timePressureDiff >= 0 && timePressureDiff <= (threshold / 2)) sssvResult = 'GOOD'
            else if (timePressureDiff > (threshold / 2) && timePressureDiff <= threshold) sssvResult = 'DEGRADED'
            else sssvResult = 'FAILED'

            this.formGroup.patchValue({ sssvResult }, { emitEvent: false });
            this.state.recalculate();
        }
    }

    onLeakRate() {
        // valveLeakRate
        const tubingPressure = this.formGroup.get('cdftTimeTubingPressure');

        const valveLeakRate$ = tubingPressure.valueChanges.subscribe(
            (pressures) => {
                let valveLeakRate = null;

                try {
                    if ((last(pressures) as any)?.tubingPressure == null || (first(pressures) as any)?.tubingPressure == null) {
                        valveLeakRate = null;
                    } else {
                        valveLeakRate =
                            (last(pressures) as any).tubingPressure -
                            (first(pressures) as any).tubingPressure;
                    }
                } catch (e) {
                    console.log('Error SSSV: ' + e);
                }

                this.formGroup.patchValue({ valveLeakRate }, { emitEvent: false });
                this.onSSSVResult();
            }
        );

        this.subscriptions.add(valveLeakRate$);
    }

    onPopulate() {
        const cdftState$ = this.state.subscribe((cdft: CDFTState) => {
            if (cdft?.cdftSssvDbt) {
                this.formGroup.patchValue(cdft.cdftSssvDbt, { emitEvent: false });

                console.log(cdft.cdftSssvDbt);
                console.log(this.formGroup.value);

                this.isSSSV = cdft.cdftSssvDbt?.isSsv;
            }
        });
        this.subscriptions.add(cdftState$);
    }

    onTypeDownholeBarrierTested() {
        if (this.wrsvType == null) return

        const editableElement = ['cyclingTest', 'clPressure', 'hydReturn', 'slamTest', 'blowingGas', 'openingIndication', 'openingIndicationPsi', 'closingIndication', 'closingIndicationPsi']

        if (this.wrsvType.toLowerCase() == 'storm choke' || this.wrsvType.toLowerCase() == 'plug') {
            editableElement.forEach(element => {
                this.formGroup.get(element).disable()
            })
        } else {
            editableElement.forEach(element => {
                this.formGroup.get(element).enable()
            })
        }
    }

    onFormGroup() {
        return this.formBuilder.group({
            id: new FormControl({ value: '', disabled: this.isViewOnly }),
            cdftRecordId: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            purposeFunctTest: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            typeOfDownholeBarrier: new FormControl({
                value: null,
                disabled: true,
            }),
            pressureBleedOff: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            pressureBleedOffPsi: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            thp: new FormControl({ value: null, disabled: this.isViewOnly }),
            wrsvType: new FormControl({
                value: null,
                disabled: true,
            }),
            wrsvOem: new FormControl({
                value: null,
                disabled: true,
            }),
            wrsvSize: new FormControl({
                value: null,
                disabled: true,
            }),
            installationAt: new FormControl({
                value: null,
                disabled: true,
            }),
            installationDepth: new FormControl({
                value: null,
                disabled: true,
            }),
            isSssv: new FormControl({
                value: null,
                disabled: true,
            }),
            isSssvSelfEqualizing: new FormControl({
                value: null,
                disabled: true,
            }),
            ssvOem: new FormControl({ value: null, disabled: true }),
            ssvSize: new FormControl({ value: null, disabled: true }),
            cyclingTest: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            clPressure: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            hydReturn: new FormControl({ value: null, disabled: this.isViewOnly }),
            slamTest: new FormControl({ value: null, disabled: this.isViewOnly }),
            blowingGas: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            openingIndication: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            openingIndicationPsi: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            closingIndication: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            closingIndicationPsi: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            valveLeakRate: new FormControl({
                value: null,
                disabled: true,
            }),
            finalWellStatus: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            sssvResult: new FormControl({
                value: 'UTT',
                disabled: false //this.isViewOnly,
            }),
            cdftTimeTubingPressure: this.formBuilder.array(
                new Array(7).fill(0).map((element, index) => { 
                    const timeAfterShutIn = 5 * index;
                    return this.formBuilder.group({
                        id: new FormControl({ value: null, disabled: true }),
                        cdftSssvDbtId: new FormControl({
                            value: null,
                            disabled: true,
                        }),
                        timeAfterShutIn: new FormControl({
                            value: timeAfterShutIn,
                            disabled: this.isViewOnly,
                        }),
                        tubingPressure: new FormControl({
                            value: null,
                            disabled: this.isViewOnly,
                        }),
                        pcpAnnulus: new FormControl({
                            value: null,
                            disabled: this.isViewOnly,
                        }),
                        pclPsi: new FormControl({
                            value: null,
                            disabled: this.isViewOnly,
                        }),
                        sclPsi: new FormControl({
                            value: null,
                            disabled: this.isViewOnly,
                        }),
                        voidAreaPsi: new FormControl({
                            value: null,
                            disabled: this.isViewOnly,
                        }),
                    });
                })
            ),
        });
    }
}
